import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { driversAPI } from '@/lib/api';
import {
  ChevronLeft, ChevronRight, SkipForward, Sparkles, Rocket,
  Target, Users, FileQuestion, UserCheck, Settings, Send,
  Globe, UserPlus, Shield, ClipboardCheck, Network, BarChart3,
  Zap, Heart, Building2, Eye, CheckCircle, Lock, CreditCard,
  FolderOpen, HelpCircle, Mail, MessageSquare, Star, ThumbsUp,
  Hash, Layers, Loader2, Wrench, AlertTriangle, FolderPlus
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Section groupings
const SECTIONS = [
  { label: 'Welcome', startId: 'welcome', color: '#5AA2AE' },
  { label: 'Configuration', startId: 'drivers', color: '#f59e0b' },
  { label: 'User Experience', startId: 'invited-users', color: '#8b5cf6' },
];

const SLIDES = [
  // === WELCOME GROUP ===
  {
    id: 'welcome', section: 'Welcome',
    title: 'Welcome to Pulse',
    subtitle: 'Connect with your teams in a whole new way',
    body: "You\u2019re about to discover a powerful new way to understand what people really think and feel about your project. Pulse goes beyond simple surveys to give you deep, actionable insight into stakeholder perception, engagement, and support.",
    image: 'https://static.prod-images.emergentagent.com/jobs/72e2dd0a-3323-4ccc-85eb-0e8f90bf294f/images/ce53e6edcc4e7fb5b7d304693edd4b453daf8fc6503a5d01de7200bcaa91822a.png',
    accent: '#5AA2AE', icon: Sparkles,
  },
  {
    id: 'projects', section: 'Welcome',
    title: 'What kind of projects?',
    subtitle: 'Anything!',
    body: "Whether you\u2019re crafting a strategy, designing a product, coding software, inventing a new medicine, or building a giant skyscraper \u2014 Pulse can help! Any project that involves people and stakeholders can benefit from understanding how those people feel and what they need.",
    image: 'https://static.prod-images.emergentagent.com/jobs/5b545d60-ee69-4c0c-908a-e03a4152de8a/images/6942d88fd03eef512d15260ae1a6a2ee22581899256497ff09bc7321b96e77b1.png',
    accent: '#f59e0b', icon: Rocket,
  },
  {
    id: 'more-than-survey', section: 'Welcome',
    title: 'So much more than a survey',
    subtitle: 'Different from any other app',
    body: "Pulse is designed to reach out across different teams, different organisations, different countries\u2026 to understand what people think and how they feel \u2014 and to measure how effective your team is at building the right perception, engagement and support.",
    image: 'https://static.prod-images.emergentagent.com/jobs/5b545d60-ee69-4c0c-908a-e03a4152de8a/images/0584a71239c47cc8d3834f6a854270b93ea5c85e43258338fe44cd4b7eb65a90.png',
    accent: '#6366f1', icon: Globe,
  },
  {
    id: 'anonymity', section: 'Welcome',
    title: 'Anonymity Thresholds',
    subtitle: 'Privacy by design',
    body: "Pulse is designed to preserve the anonymity of individual user responses. Responses are always grouped for analysis \u2014 never shown individually. The minimum anonymity threshold is 3, which means if any segment has fewer than three responses, the results will be hidden entirely. This ensures people feel safe to share their honest feedback.",
    image: 'https://static.prod-images.emergentagent.com/jobs/5b545d60-ee69-4c0c-908a-e03a4152de8a/images/8ddde32bea856a1bcb1274aa8dc1922a4c323dd68414bf31399e569b12042d26.png',
    accent: '#10b981', icon: Shield,
    highlights: ['Minimum Responses', 'Grouped Responses', 'AI-Powered Summarisation', 'Redaction'],
  },
  {
    id: 'always-on', section: 'Welcome',
    title: 'Always On',
    subtitle: 'Measure change over time',
    body: "Pulse is always on \u2014 which means people can log back on to change their mind at any time, so we can measure if your communications and engagement are having the desired effect. Pulse asks targeted questions based on every person\u2019s role \u2014 make sure the effort required by the user is low, but the value from the insight is high.",
    image: 'https://static.prod-images.emergentagent.com/jobs/5b545d60-ee69-4c0c-908a-e03a4152de8a/images/1f08b3840f6fa3147885968aff35480fac93b723bdce64e80d0f6c113f669619.png',
    accent: '#10b981', icon: Zap,
  },
  // === CONFIGURATION GROUP ===
  {
    id: 'drivers', section: 'Configuration',
    title: 'Drivers',
    subtitle: 'The big categories of what you measure',
    body: "Drivers are the key themes or dimensions you want to analyse. This can be anything: Sentiment, Engagement, Confidence, and more. Questions are organised using these Drivers, creating structure for your analysis.",
    image: 'https://static.prod-images.emergentagent.com/jobs/72e2dd0a-3323-4ccc-85eb-0e8f90bf294f/images/4605be1eb34440e4e92c83b3865f5c87465aff32a5f214a5b69a7b4dc5bf7fa9.png',
    accent: '#5AA2AE', icon: Target,
    highlights: ['Sentiment', 'Engagement', 'Confidence', 'Information Needs', 'Culture', 'Interest', 'Relationships', 'Communications', 'Influence', 'Internal Politics', 'Ways of Working', 'Improvement', 'Community', 'Alignment'],
  },
  {
    id: 'segments', section: 'Configuration',
    title: 'Segments',
    subtitle: 'Categorise the people in your project',
    body: "Segments represent different categories of people involved in your projects. People can belong to multiple segments \u2014 someone might work in one organisation but sit across two project teams. Remember the Anonymity Threshold: design your segments so each has more than 3 people to ensure results are visible.",
    image: 'https://static.prod-images.emergentagent.com/jobs/5b545d60-ee69-4c0c-908a-e03a4152de8a/images/4876d1cf2c0b50f91f3eb462568a08585386b2127ad9cbe9db94ee363af317fc.png',
    accent: '#8b5cf6', icon: Users,
    highlights: ['Organizations', 'Locations', 'Project Teams', 'Involvement', 'Markets', 'Disciplines', 'Level', 'Seniority', 'Neighborhood'],
  },
  {
    id: 'questions', section: 'Configuration',
    title: 'Questions',
    subtitle: 'Targeted questions for each Driver',
    body: "In each Driver category, you can create a set of questions and target them at different users based on their Segment assignments. Choose from a range of question types to gather exactly the feedback you need.",
    image: 'https://static.prod-images.emergentagent.com/jobs/5b545d60-ee69-4c0c-908a-e03a4152de8a/images/df197e1cafea5f6278b94cfda80607e050ed1a026e740e39a015c1127fe79665.png',
    accent: '#0ea5e9', icon: FileQuestion,
    subCarousel: 'questions',
  },
  {
    id: 'stakeholders', section: 'Configuration',
    title: 'Stakeholders',
    subtitle: 'The people who matter to your project',
    body: "These are the actual people relevant to your project. People are identified through three Stakeholder Pathways: an administrator adds them directly, other users nominate them, or people identify themselves through a public link or QR code.",
    image: 'https://static.prod-images.emergentagent.com/jobs/5b545d60-ee69-4c0c-908a-e03a4152de8a/images/a4448bb4223d950e6520c5576d0fa371b7d42a79141e868db97fd9f08200510c.png',
    accent: '#f97316', icon: UserPlus,
  },
  {
    id: 'you-decide', section: 'Configuration',
    title: 'You Decide',
    subtitle: 'Control who participates and how',
    body: "Once users are added through any pathway, the Admin confirms their segment assignments and determines their status.",
    image: 'https://static.prod-images.emergentagent.com/jobs/72e2dd0a-3323-4ccc-85eb-0e8f90bf294f/images/8d8c9a273a7b0cd493650f8749ad47d7c3c322827082f7eb84393b4051e5cb50.png',
    accent: '#5AA2AE', icon: Settings,
    highlights: ['Invited \u2014 Sent an invitation to participate', 'Reference \u2014 In the register, but not invited', 'Rejected \u2014 Not invited, not added'],
  },
  {
    id: 'roles-admin', section: 'Configuration',
    title: 'Roles & Administration',
    subtitle: 'Account Owners and Administrators',
    body: "Pulse subscription cost is based on the number of unique invited users within an account. As the Account Owner, you manage the projects inside your account and oversee your subscription. You can also nominate other users as Administrators \u2014 they\u2019ll be able to configure questions, manage stakeholders, and view dashboards on your behalf. You can remove this privilege at any time.",
    image: 'https://static.prod-images.emergentagent.com/jobs/72e2dd0a-3323-4ccc-85eb-0e8f90bf294f/images/bce8c38701ce03ebf365ab12885475a3fea3cac243a4e48a4a7254b87a28677d.png',
    accent: '#f59e0b', icon: CreditCard,
    highlights: ['Account Owner \u2014 Manages subscription, creates projects, full control', 'Administrators \u2014 Delegated access to configure and manage projects'],
  },
  {
    id: 'multiple-projects', section: 'Configuration',
    title: 'Multiple Projects',
    subtitle: 'Scale across your organisation',
    body: "As an Account Owner, you can create multiple projects based on your license tier. Your users may be invited to multiple projects within your account, or they may be invited to other projects managed by others \u2014 but you only pay based on unique invited users in your own account.",
    image: 'https://static.prod-images.emergentagent.com/jobs/72e2dd0a-3323-4ccc-85eb-0e8f90bf294f/images/7fdc849c79b992c39dcd4f92f56ac3af88f69a737f15041c3c5da793771a723b.png',
    accent: '#6366f1', icon: FolderOpen,
  },
  {
    id: 'invitations', section: 'Configuration',
    title: 'Invitations',
    subtitle: 'Reach out when you are ready',
    body: "Once you are ready, you can issue invitations to the people you have chosen to invite. Pulse handles the email delivery and tracks who has responded, making it easy to follow up with reminders.",
    image: 'https://static.prod-images.emergentagent.com/jobs/72e2dd0a-3323-4ccc-85eb-0e8f90bf294f/images/a963adbc230d0f90ce27c8fa9e36cf4c0a603a7d8859f692a678e2676c2640a6.png',
    accent: '#ec4899', icon: Send,
  },
  {
    id: 'public-landing', section: 'Configuration',
    title: 'Public Landing',
    subtitle: 'Open feedback via link or QR code',
    body: "Pulse allows you to issue a generic link or QR code accessible by anyone you choose to share it with. This creates a special set of questions allowing people to provide anonymous feedback, or if they choose to identify themselves, they\u2019ll be sent to the admin for review.",
    image: 'https://static.prod-images.emergentagent.com/jobs/72e2dd0a-3323-4ccc-85eb-0e8f90bf294f/images/15ec30021e424d2268507ecca870d15f08ed89e888cbd13b6b4b65cb124cd6f3.png',
    accent: '#14b8a6', icon: Globe,
  },
  {
    id: 'permissions', section: 'Configuration',
    title: 'Permissions',
    subtitle: 'Control what users can see',
    body: "You can configure which dashboards are available to users and what they are able to see based on their segments. This gives you full control over the experience \u2014 ensuring people only see insights relevant to their role.",
    image: 'https://static.prod-images.emergentagent.com/jobs/72e2dd0a-3323-4ccc-85eb-0e8f90bf294f/images/2b3bec7f0843aad2f4a16a99b09e783de7f94a33f730e686b29a107afa27ea0e.png',
    accent: '#5AA2AE', icon: Eye,
  },
  // === USER EXPERIENCE GROUP ===
  {
    id: 'invited-users', section: 'User Experience',
    title: 'Invited Users',
    subtitle: 'Their journey begins',
    body: "Once they receive an invite, users will be asked to create an account and begin the Pulse process. The experience is designed to be welcoming, easy to understand, and quick to complete.",
    image: 'https://static.prod-images.emergentagent.com/jobs/72e2dd0a-3323-4ccc-85eb-0e8f90bf294f/images/b30348bd933b9ae2c050e89361eb3eddbd96f07866f480c10f3ccde175a89f03.png',
    accent: '#6366f1', icon: UserCheck,
  },
  {
    id: 'user-onboarding', section: 'User Experience',
    title: 'User Onboarding',
    subtitle: 'A safe space to speak up',
    body: "Once they arrive, users are greeted with a quick introduction informing them about how Pulse works, and that it\u2019s safe to speak up and share their honest feedback. Building trust from the very first interaction is at the heart of Pulse.",
    image: 'https://static.prod-images.emergentagent.com/jobs/5b545d60-ee69-4c0c-908a-e03a4152de8a/images/8c4f0f34b02bb6afda01b8bd62b42bc2b8b52ad75115560bb64e94f8fd075d1c.png',
    accent: '#10b981', icon: Heart,
  },
  {
    id: 'about-me', section: 'User Experience',
    title: 'About Me',
    subtitle: 'Personal reflection questions',
    body: "Users walk through the questions they have been assigned based on their segments. They can log back in at any time \u2014 you can set which ones are \u201crequired\u201d and which ones can be optionally skipped. It\u2019s a personal, reflective experience.",
    image: 'https://static.prod-images.emergentagent.com/jobs/72e2dd0a-3323-4ccc-85eb-0e8f90bf294f/images/1cce67f33f20f9e7dc595ab4a70c50ce86a2b03dcc22a491c447bb9610d3dd9c.png',
    accent: '#5AA2AE', icon: ClipboardCheck,
  },
  {
    id: 'about-others', section: 'User Experience',
    title: 'About Others',
    subtitle: 'Peer perception mapping',
    body: "You may use the About Others feature, which allows users to identify the stakeholders they feel are important to the project, and answer a few questions about their perception of those stakeholders. This builds out a rich picture of relationships.",
    image: 'https://static.prod-images.emergentagent.com/jobs/72e2dd0a-3323-4ccc-85eb-0e8f90bf294f/images/669a5ba4a7ae127f347c48da422dc2181a2e3a9581a395fb4a879e7e5a927586.png',
    accent: '#8b5cf6', icon: Users,
  },
  {
    id: 'mapping-categories', section: 'User Experience',
    title: 'Mapping Categories',
    subtitle: 'Building the People Galaxy',
    body: "Mapping categories are used in About Others to model relationships between people. It is a way for users to identify how other people relate to them, building out a large network we call the People Galaxy. This network enables some of Pulse\u2019s most advanced analysis.",
    image: 'https://static.prod-images.emergentagent.com/jobs/72e2dd0a-3323-4ccc-85eb-0e8f90bf294f/images/af9f336a44e7b60efa797098cdd7e635172d53b04c14a27b52c3d81548dbf494.png',
    accent: '#6366f1', icon: Network,
  },
  {
    id: 'dashboards', section: 'User Experience',
    title: 'Dashboards',
    subtitle: 'Insights that drive decisions',
    body: "Once the data starts flowing in, Pulse\u2019s dashboards bring everything to life. Explore powerful analysis views designed to help you make better decisions.",
    image: 'https://static.prod-images.emergentagent.com/jobs/5b545d60-ee69-4c0c-908a-e03a4152de8a/images/eb29b7471f962256fa5eacc1c252fdb745e662de411b35265b33dfcd06a2f448.png',
    accent: '#5AA2AE', icon: BarChart3,
    subCarousel: 'dashboards',
  },
  {
    id: 'additional-help', section: 'User Experience',
    title: 'Additional Help',
    subtitle: 'We\u2019re here for you',
    body: "If you need dedicated support to implement Pulse, feel free to contact us \u2014 and we can put you in touch with one of our implementation partners. They have deep expertise in stakeholder engagement and can help you get the most out of Pulse.",
    image: 'https://static.prod-images.emergentagent.com/jobs/72e2dd0a-3323-4ccc-85eb-0e8f90bf294f/images/52382b10d9f4ffdd7613ea8630f870e5edc67015bed2c8f6e3ffe17ee6e3b0a6.png',
    accent: '#f97316', icon: HelpCircle,
  },
  // === READY TO GO ===
  {
    id: 'ready-to-go', section: 'User Experience',
    title: 'Ready to Go?',
    subtitle: 'Choose how to get started',
    body: "You can use Quick Start to pre-populate your project with a set of frequently used questions and drivers \u2014 a great way to explore how Pulse works. Or, if you prefer, configure everything manually from scratch.",
    image: null,
    accent: '#5AA2AE', icon: Rocket,
    isReadyToGo: true,
  },
];

// Question type examples for sub-carousel
const QUESTION_TYPES = [
  { name: 'Slider', icon: '|||', desc: 'Users drag a handle along a scale to indicate their position between two endpoints.',
    example: (
      <div className="space-y-2 p-3">
        <p className="text-xs font-medium text-slate-700">How confident are you in the project timeline?</p>
        <Slider value={[6.5]} min={0} max={10} step={0.1} className="my-2" />
        <div className="flex justify-between text-[10px] text-slate-400"><span>Not Confident</span><span>Very Confident</span></div>
      </div>
    )},
  { name: 'Multiple Choice', icon: CheckCircle, desc: 'Select one option from a predefined list.',
    example: (
      <div className="space-y-2 p-3">
        <p className="text-xs font-medium text-slate-700">What best describes your role?</p>
        <div className="space-y-1.5">{['Decision Maker', 'Advisor', 'Team Member', 'Observer'].map((o, i) => (
          <div key={o} className={`flex items-center gap-2 p-2 rounded-md border text-xs ${i === 2 ? 'border-[#5AA2AE] bg-[#5AA2AE]/5' : 'border-slate-200'}`}>
            <div className={`w-3.5 h-3.5 rounded-full border-2 ${i === 2 ? 'border-[#5AA2AE] bg-[#5AA2AE]' : 'border-slate-300'}`}>{i === 2 && <div className="w-1.5 h-1.5 bg-white rounded-full m-auto mt-[3px]"/>}</div>
            {o}
          </div>
        ))}</div>
      </div>
    )},
  { name: 'Yes / No', icon: ThumbsUp, desc: 'Simple binary choice for clear-cut questions.',
    example: (
      <div className="space-y-2 p-3">
        <p className="text-xs font-medium text-slate-700">Do you feel your voice is heard in this project?</p>
        <div className="flex gap-2 mt-2">
          <div className="flex-1 p-2.5 rounded-lg border-2 border-[#5AA2AE] bg-[#5AA2AE]/5 text-center text-xs font-medium text-[#5AA2AE]">Yes</div>
          <div className="flex-1 p-2.5 rounded-lg border-2 border-slate-200 text-center text-xs font-medium text-slate-500">No</div>
        </div>
      </div>
    )},
  { name: 'Free Text', icon: MessageSquare, desc: 'Open-ended response for detailed qualitative feedback.',
    example: (
      <div className="space-y-2 p-3">
        <p className="text-xs font-medium text-slate-700">What would improve your experience with this project?</p>
        <div className="border rounded-md p-2.5 text-xs text-slate-400 min-h-[60px] bg-slate-50">Type your response here...</div>
      </div>
    )},
  { name: 'Dropdown', icon: ChevronLeft, desc: 'Select from a collapsible list of options.',
    example: (
      <div className="space-y-2 p-3">
        <p className="text-xs font-medium text-slate-700">Which department are you from?</p>
        <div className="border rounded-md p-2.5 text-xs text-slate-700 bg-white flex justify-between items-center">
          Engineering <ChevronLeft className="w-3 h-3 -rotate-90 text-slate-400" />
        </div>
      </div>
    )},
  { name: 'Multi-Topic', icon: Hash, desc: 'Ranked list of topics for prioritising key themes and areas of focus.',
    example: (
      <div className="space-y-2 p-3">
        <p className="text-xs font-medium text-slate-700">Rank the following topics by importance:</p>
        <div className="space-y-1.5">{['Communication', 'Leadership', 'Resources', 'Timeline'].map((t, i) => (
          <div key={t} className="flex items-center gap-2 p-2 rounded-md border border-slate-200 text-xs bg-white">
            <div className="w-5 h-5 rounded-md bg-[#5AA2AE]/10 text-[#5AA2AE] flex items-center justify-center text-[10px] font-bold">{i + 1}</div>
            <span className="text-slate-700">{t}</span>
            <Layers className="w-3 h-3 text-slate-300 ml-auto" />
          </div>
        ))}</div>
      </div>
    )},
];

// Dashboard types for sub-carousel
const DASHBOARD_TYPES = [
  { name: 'Project Summary', icon: BarChart3, color: '#5AA2AE',
    desc: 'High-level overview of your project health with AI-generated insights, key metrics, and response trends.',
    visual: (
      <div className="bg-slate-50 rounded-lg p-3 space-y-2">
        <div className="flex gap-2">{['8.2', '7.4', '6.8'].map((v, i) => (
          <div key={i} className="flex-1 rounded-md bg-white border p-2 text-center"><div className="text-lg font-bold" style={{color: '#5AA2AE'}}>{v}</div><div className="text-[9px] text-slate-400">{['Sentiment', 'Engagement', 'Confidence'][i]}</div></div>
        ))}</div>
        <div className="h-12 bg-gradient-to-r from-[#5AA2AE]/20 via-[#5AA2AE]/40 to-[#5AA2AE]/20 rounded-md flex items-end px-2 pb-1 gap-1">
          {[40,65,55,70,60,75,68].map((h,i) => <div key={i} className="flex-1 rounded-t" style={{height: `${h}%`, backgroundColor: '#5AA2AE', opacity: 0.6 + i*0.05}} />)}
        </div>
      </div>
    )},
  { name: 'Driver Analysis', icon: Target, color: '#f59e0b',
    desc: 'Deep-dive into each driver with question-level scores, trends, and cross-segment comparison.',
    visual: (
      <div className="bg-slate-50 rounded-lg p-3 space-y-2">
        {['Communication', 'Leadership', 'Trust'].map((d, i) => (
          <div key={d} className="flex items-center gap-2 bg-white border rounded-md p-2">
            <div className="text-xs font-medium text-slate-700 w-24 truncate">{d}</div>
            <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{width: `${[78,65,82][i]}%`, backgroundColor: '#f59e0b'}} /></div>
            <span className="text-xs font-bold" style={{color: '#f59e0b'}}>{[7.8, 6.5, 8.2][i]}</span>
          </div>
        ))}
      </div>
    )},
  { name: 'Segment Analysis', icon: Users, color: '#8b5cf6',
    desc: 'Heatmap view comparing scores across different segments, revealing patterns and outliers.',
    visual: (
      <div className="bg-slate-50 rounded-lg p-3">
        <div className="grid grid-cols-4 gap-1 text-[9px]">
          <div />{['Eng', 'Ops', 'Sales'].map(s => <div key={s} className="text-center font-medium text-slate-500">{s}</div>)}
          {['Sentiment', 'Engage.', 'Trust'].map((q, qi) => (
            <React.Fragment key={q}>
              <div className="font-medium text-slate-600 truncate pr-1">{q}</div>
              {[[82,65,74],[70,78,60],[85,72,68]][qi].map((v, vi) => (
                <div key={vi} className="rounded-md p-1.5 text-center font-bold text-[10px]"
                  style={{backgroundColor: v > 75 ? '#dcfce7' : v > 65 ? '#fef3c7' : '#fee2e2', color: v > 75 ? '#15803d' : v > 65 ? '#a16207' : '#dc2626'}}>{v}%</div>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>
    )},
  { name: 'Public Analysis', icon: Globe, color: '#14b8a6',
    desc: 'Analyse anonymous feedback from public respondents alongside invited stakeholder data.',
    visual: (
      <div className="bg-slate-50 rounded-lg p-3 space-y-2">
        <div className="flex gap-2">
          <div className="flex-1 rounded-md bg-white border p-2 text-center"><div className="text-lg font-bold text-[#14b8a6]">47</div><div className="text-[9px] text-slate-400">Responses</div></div>
          <div className="flex-1 rounded-md bg-white border p-2 text-center"><div className="text-lg font-bold text-[#14b8a6]">7.1</div><div className="text-[9px] text-slate-400">Avg Score</div></div>
        </div>
        <div className="h-8 bg-white border rounded-md flex items-center px-2 gap-0.5">
          {[20,35,25,15,5].map((w,i) => <div key={i} className="h-4 rounded" style={{width:`${w}%`, backgroundColor: ['#dcfce7','#bbf7d0','#fef3c7','#fed7aa','#fee2e2'][i]}} />)}
        </div>
      </div>
    )},
  { name: 'People Galaxy', icon: Network, color: '#6366f1',
    desc: 'Interactive force-directed graph showing the network of relationships between stakeholders.',
    visual: (
      <div className="bg-slate-900 rounded-lg p-3 h-24 relative overflow-hidden">
        {[{x:30,y:20,s:8},{x:70,y:35,s:6},{x:45,y:60,s:10},{x:15,y:50,s:5},{x:80,y:65,s:7},{x:55,y:25,s:5},{x:25,y:75,s:6}].map((n,i) => (
          <React.Fragment key={i}>
            <div className="absolute rounded-full" style={{left:`${n.x}%`,top:`${n.y}%`,width:n.s*2,height:n.s*2,backgroundColor:'#818cf8',boxShadow:'0 0 8px #818cf8'}} />
            {i > 0 && <svg className="absolute inset-0 w-full h-full"><line x1={`${[{x:30,y:20},{x:70,y:35},{x:45,y:60},{x:15,y:50},{x:80,y:65},{x:55,y:25},{x:25,y:75}][i-1].x}%`} y1={`${[{x:30,y:20},{x:70,y:35},{x:45,y:60},{x:15,y:50},{x:80,y:65},{x:55,y:25},{x:25,y:75}][i-1].y}%`} x2={`${n.x}%`} y2={`${n.y}%`} stroke="#818cf8" strokeOpacity="0.3" strokeWidth="1" /></svg>}
          </React.Fragment>
        ))}
      </div>
    )},
  { name: 'Matrix', icon: Target, color: '#f97316',
    desc: 'Scatter plot analysis with configurable axes, preset backgrounds, and segment overlays.',
    visual: (
      <div className="bg-slate-50 rounded-lg p-3 h-24 relative overflow-hidden">
        <div className="absolute inset-3 border-l-2 border-b-2 border-slate-300">
          {[{x:25,y:60},{x:45,y:30},{x:65,y:45},{x:35,y:20},{x:75,y:55},{x:55,y:70},{x:20,y:40},{x:80,y:25}].map((p,i) => (
            <div key={i} className="absolute w-3 h-3 rounded-full" style={{left:`${p.x}%`,bottom:`${p.y}%`,backgroundColor:['#5AA2AE','#f59e0b','#8b5cf6','#5AA2AE','#f59e0b','#8b5cf6','#5AA2AE','#f59e0b'][i],opacity:0.7}} />
          ))}
        </div>
      </div>
    )},
];

// Sub-carousel component
function SubCarousel({ items, type }) {
  const [idx, setIdx] = useState(0);
  const item = items[idx];
  return (
    <div className="mt-4 bg-white/80 backdrop-blur rounded-xl border border-slate-200 overflow-hidden" data-testid={`sub-carousel-${type}`}>
      {/* Tabs */}
      <div className="flex overflow-x-auto border-b bg-slate-50/80 p-1 gap-1">
        {items.map((it, i) => (
          <button key={i} onClick={() => setIdx(i)}
            className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${i === idx ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}>
            {it.name}
          </button>
        ))}
      </div>
      {/* Content */}
      <div className="p-4">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${item.color || '#5AA2AE'}15`, color: item.color || '#5AA2AE' }}>
            {typeof item.icon === 'string' ? <span className="text-sm font-bold">{item.icon}</span> : item.icon && <item.icon className="w-4 h-4" />}
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-900">{item.name}</h4>
            <p className="text-xs text-slate-500 mt-0.5">{item.desc}</p>
          </div>
        </div>
        {item.example || item.visual}
      </div>
      {/* Nav dots */}
      <div className="flex justify-center gap-1.5 pb-3">
        {items.map((_, i) => (
          <button key={i} onClick={() => setIdx(i)}
            className={`rounded-full transition-all ${i === idx ? 'w-5 h-1.5 bg-slate-900' : 'w-1.5 h-1.5 bg-slate-300'}`} />
        ))}
      </div>
    </div>
  );
}

// Floating shapes
function FloatingShapes({ color }) {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-[0.07] animate-pulse" style={{ backgroundColor: color }} />
      <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full opacity-[0.05]" style={{ backgroundColor: color, animation: 'pulse 3s ease-in-out infinite 0.5s' }} />
    </div>
  );
}

// Walkthrough overlay for Admin Dashboard
const WALKTHROUGH_STEPS = [
  { id: 'projects', label: 'Projects', description: 'View and manage all your projects. Create new projects, see stats, and configure settings.', icon: FolderOpen, selector: '[data-testid="nav-projects"]' },
  { id: 'project-selector', label: 'Project Selector', description: 'Switch between projects using the dropdown in the sidebar. Select a project to access its configuration and dashboards.', icon: ChevronRight, selector: '[data-testid="project-selector"]' },
  { id: 'about-me', label: 'About Me', description: 'Where users answer personal reflection questions assigned based on their segments.', icon: ClipboardCheck, selector: '[data-testid="nav-user-about-me"]' },
  { id: 'about-others', label: 'About Others', description: 'Users identify and rate stakeholders they work with, building the relationship network.', icon: Users, selector: '[data-testid="nav-user-about-others"]' },
  { id: 'dashboards', label: 'Dashboards', description: 'Explore powerful analytics: Project Summary, Driver Analysis, Segment Heatmaps, Matrix View, and People Galaxy.', icon: BarChart3, selector: '[data-testid="nav-dashboards-group"]' },
  { id: 'administration', label: 'Administration', description: 'Configure drivers, segments, questions, stakeholders, permissions, and email settings.', icon: Settings, selector: '[data-testid="nav-admin-group"]' },
  { id: 'help', label: 'Help & Support', description: 'Access the setup guide, documentation, and contact our support team for assistance.', icon: HelpCircle, selector: '[data-testid="help-page-link"]' },
  { id: 'manage-account', label: 'Manage Account', description: 'View your subscription, billing, invoices, and manage your account settings.', icon: CreditCard, selector: '[data-testid="manage-account-link"]' },
];

// User walkthrough steps (non-admin)
const USER_WALKTHROUGH_STEPS = [
  { id: 'about-me', label: 'About Me', description: 'Start here! Answer questions about your own experience and perceptions. Your honest feedback builds the foundation for meaningful insights.', icon: ClipboardCheck, selector: '[data-testid="nav-user-about-me"]' },
  { id: 'about-others', label: 'About Others', description: 'Rate and provide feedback on stakeholders you work with. This builds a 360-degree view of relationships and collaboration.', icon: Users, selector: '[data-testid="nav-user-about-others"]' },
  { id: 'dashboards', label: 'Dashboards', description: 'Once enough responses are gathered, explore interactive dashboards with aggregated insights, trends, and AI-powered analysis.', icon: BarChart3, selector: '[data-testid="nav-dashboards-group"]' },
  { id: 'project-selector', label: 'Switch Projects', description: 'If you\'re part of multiple projects, use this dropdown to switch between them at any time.', icon: ChevronRight, selector: '[data-testid="project-selector"]' },
];

function WalkthroughOverlay({ onComplete, steps }) {
  const activeSteps = steps || WALKTHROUGH_STEPS;
  const [step, setStep] = useState(0);
  const [spotlightRect, setSpotlightRect] = useState(null);
  const current = activeSteps[step];
  const isLast = step === activeSteps.length - 1;

  // Find and highlight the target element
  useEffect(() => {
    const findTarget = () => {
      const el = document.querySelector(current.selector);
      if (el) {
        const rect = el.getBoundingClientRect();
        setSpotlightRect({
          top: rect.top - 6,
          left: rect.left - 6,
          width: rect.width + 12,
          height: rect.height + 12,
        });
        // Scroll element into view if needed
        el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      } else {
        setSpotlightRect(null);
      }
    };
    findTarget();
    // Re-check after a short delay for dynamic elements
    const timer = setTimeout(findTarget, 300);
    return () => clearTimeout(timer);
  }, [step, current.selector]);

  // Calculate tooltip position
  // On mobile: position below the spotlight. On desktop: to the right
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const tooltipStyle = spotlightRect ? (
    isMobile ? {
      position: 'fixed',
      bottom: 16,
      left: 16,
      right: 16,
      zIndex: 110,
    } : {
      position: 'fixed',
      top: Math.max(20, Math.min(spotlightRect.top - 20, window.innerHeight - 320)),
      left: spotlightRect.left + spotlightRect.width + 24,
      zIndex: 110,
    }
  ) : {};

  const isCentered = !spotlightRect;

  return (
    <div className="fixed inset-0 z-[100]" data-testid="walkthrough-overlay">
      {/* Dark overlay with spotlight cutout */}
      <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 101 }}>
        <defs>
          <mask id="walkthrough-mask">
            <rect width="100%" height="100%" fill="white" />
            {spotlightRect && (
              <rect
                x={spotlightRect.left} y={spotlightRect.top}
                width={spotlightRect.width} height={spotlightRect.height}
                rx="8" fill="black"
              />
            )}
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(0,0,0,0.6)" mask="url(#walkthrough-mask)" />
      </svg>

      {/* Spotlight border/glow ring */}
      {spotlightRect && (
        <div className="fixed rounded-lg border-2 border-[#5AA2AE] shadow-[0_0_0_4px_rgba(90,162,174,0.3)] transition-all duration-300 pointer-events-none"
          style={{ top: spotlightRect.top, left: spotlightRect.left, width: spotlightRect.width, height: spotlightRect.height, zIndex: 102 }}>
          <div className="absolute inset-0 rounded-lg animate-pulse" style={{ boxShadow: '0 0 20px rgba(90,162,174,0.4)' }} />
        </div>
      )}

      {/* Arrow from spotlight to card - hidden on mobile */}
      {spotlightRect && !isMobile && (
        <div className="fixed pointer-events-none" style={{
          top: spotlightRect.top + spotlightRect.height / 2 - 8,
          left: spotlightRect.left + spotlightRect.width + 4,
          zIndex: 109,
        }}>
          <ChevronRight className="w-5 h-5 text-[#5AA2AE] animate-pulse" />
        </div>
      )}

      {/* Tooltip card */}
      <div className={`${isCentered ? 'fixed inset-0 flex items-center justify-center' : ''}`}
        style={{ zIndex: 110 }}>
        <div className={`bg-white rounded-2xl shadow-2xl ${isMobile ? 'w-full' : 'w-80'} overflow-hidden animate-in fade-in slide-in-from-left-4 duration-300`}
          style={!isCentered ? tooltipStyle : {}}>
          {/* Header */}
          <div className="px-5 pt-5 pb-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">Quick Tour</span>
              <span className="text-xs text-slate-400">{step + 1} / {activeSteps.length}</span>
            </div>
            <div className="h-1 bg-slate-100 rounded-full overflow-hidden mb-3">
              <div className="h-full bg-[#5AA2AE] rounded-full transition-all duration-500" style={{ width: `${((step + 1) / activeSteps.length) * 100}%` }} />
            </div>
          </div>

          {/* Content */}
          <div className="px-5 pb-3">
            <div className="flex items-center gap-2.5 mb-2">
              <div className="w-10 h-10 rounded-xl bg-[#5AA2AE]/10 flex items-center justify-center flex-shrink-0">
                <current.icon className="w-5 h-5 text-[#5AA2AE]" />
              </div>
              <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>{current.label}</h3>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed">{current.description}</p>
          </div>

          {/* Step indicators */}
          <div className="px-5 pb-2">
            <div className="flex gap-1.5 justify-center">
              {activeSteps.map((_, i) => (
                <button key={i} onClick={() => setStep(i)}
                  className={`rounded-full transition-all ${i === step ? 'w-5 h-1.5 bg-[#5AA2AE]' : i < step ? 'w-1.5 h-1.5 bg-[#5AA2AE]/40' : 'w-1.5 h-1.5 bg-slate-200'}`} />
              ))}
            </div>
          </div>

          {/* Buttons */}
          <div className="flex items-center justify-between px-5 py-3 bg-slate-50 border-t">
            <Button variant="ghost" size="sm" onClick={onComplete} className="text-slate-400 hover:text-slate-600 text-xs" data-testid="walkthrough-skip">
              Skip tour
            </Button>
            <div className="flex gap-2">
              {step > 0 && (
                <Button variant="outline" size="sm" onClick={() => setStep(step - 1)} data-testid="walkthrough-prev">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
              )}
              <Button size="sm" onClick={() => isLast ? onComplete() : setStep(step + 1)}
                className="bg-[#5AA2AE] hover:bg-[#4a8f9a] text-white gap-1 min-w-[90px]"
                data-testid="walkthrough-next">
                {isLast ? <><CheckCircle className="w-4 h-4" /> Done!</> : <>Next <ChevronRight className="w-4 h-4" /></>}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminOnboardingCarousel() {
  const navigate = useNavigate();
  const { projectId: urlProjectId } = useParams();
  const { token } = useAuth();
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [quickStartStatus, setQuickStartStatus] = useState({ eligible: true, loading: true });
  const [importingQuickStart, setImportingQuickStart] = useState(false);
  const touchStartX = useRef(0);

  // Project creation state
  const [activeProjectId, setActiveProjectId] = useState(urlProjectId || null);
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'quickstart' or 'manual'
  const [projectForm, setProjectForm] = useState({ name: '', description: '' });
  const [creatingProject, setCreatingProject] = useState(false);

  // Check Quick Start eligibility on mount or when project changes
  useEffect(() => {
    if (!activeProjectId) {
      setQuickStartStatus({ eligible: true, loading: false });
      return;
    }
    (async () => {
      try {
        const res = await driversAPI.getQuickStartStatus(activeProjectId);
        setQuickStartStatus({ eligible: res.data.eligible, loading: false, ...res.data });
      } catch {
        setQuickStartStatus({ eligible: false, loading: false });
      }
    })();
  }, [activeProjectId]);

  // Create project, then proceed with the pending action
  const handleCreateProject = async () => {
    if (!projectForm.name.trim()) { toast.error('Please enter a project name'); return; }
    setCreatingProject(true);
    try {
      const res = await fetch(`${API_URL}/api/projects`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: projectForm.name, description: projectForm.description }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Failed to create project'); }
      const project = await res.json();
      const newPid = project.id;
      setActiveProjectId(newPid);
      setShowCreateProject(false);
      toast.success(`Project "${project.name}" created`);

      if (pendingAction === 'quickstart') {
        // Import Quick Start data into the new project
        try {
          const qsRes = await driversAPI.importFullQuickStart(newPid);
          const { imported_drivers, imported_questions } = qsRes.data;
          toast.success(`Imported ${imported_drivers} driver(s) and ${imported_questions} question(s)`);
        } catch (err) {
          toast.error('Quick Start import failed — you can add content manually');
        }
      }

      // Navigate to admin dashboard and trigger walkthrough
      navigate(`/project/${newPid}/admin-dashboard`);
      setTimeout(() => window.dispatchEvent(new CustomEvent('start-walkthrough')), 600);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreatingProject(false);
    }
  };

  const handleQuickStart = async () => {
    if (!activeProjectId) {
      if (!token) { navigate('/login'); return; }
      setPendingAction('quickstart');
      setShowCreateProject(true);
      return;
    }
    setImportingQuickStart(true);
    try {
      const res = await driversAPI.importFullQuickStart(activeProjectId);
      const { imported_drivers, imported_questions } = res.data;
      toast.success(`Imported ${imported_drivers} driver(s) and ${imported_questions} question(s)`);
      setQuickStartStatus(prev => ({ ...prev, eligible: false }));
      navigate(`/project/${activeProjectId}/admin-dashboard`);
      setTimeout(() => window.dispatchEvent(new CustomEvent('start-walkthrough')), 600);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to import Quick Start data';
      toast.error(msg);
    } finally {
      setImportingQuickStart(false);
    }
  };

  const handleManual = () => {
    if (!activeProjectId) {
      if (!token) { navigate('/login'); return; }
      setPendingAction('manual');
      setShowCreateProject(true);
      return;
    }
    navigate(`/project/${activeProjectId}/admin-dashboard`);
    setTimeout(() => window.dispatchEvent(new CustomEvent('start-walkthrough')), 600);
  };

  const slide = SLIDES[current];
  const isFirst = current === 0;
  const isLast = current === SLIDES.length - 1;

  const goTo = (index) => {
    if (animating || index === current || index < 0 || index >= SLIDES.length) return;
    setDirection(index > current ? 1 : -1);
    setAnimating(true);
    setTimeout(() => { setCurrent(index); setAnimating(false); }, 200);
  };

  const next = () => {
    if (!isLast) {
      goTo(current + 1);
    } else {
      // Navigate to dashboard and trigger walkthrough
      navigate('/dashboard');
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('start-walkthrough'));
      }, 500);
    }
  };
  const prev = () => { if (!isFirst) goTo(current - 1); };

  useEffect(() => {
    const handler = (e) => { if (e.key === 'ArrowRight') next(); if (e.key === 'ArrowLeft') prev(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) { diff > 0 ? next() : prev(); }
  };

  const slideClass = animating
    ? direction > 0 ? 'opacity-0 translate-x-8' : 'opacity-0 -translate-x-8'
    : 'opacity-100 translate-x-0';

  // Build section groups for dot navigation
  const sectionGroups = SECTIONS.map(sec => ({
    ...sec,
    slides: SLIDES.map((s, i) => ({ ...s, index: i })).filter(s => s.section === sec.label),
  }));

  return (
    <TooltipProvider delayDuration={200}>
      <div className="min-h-screen bg-white flex flex-col relative overflow-hidden"
        data-testid="admin-onboarding-carousel"
        onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <FloatingShapes color={slide.accent} />

        {/* Header */}
        <header className="flex justify-between items-center px-4 md:px-8 py-3 relative z-10">
          <img src="/pulse-logo-with-name.png" alt="Pulse" className="h-9 object-contain" />
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-xs text-slate-400">{current + 1} / {SLIDES.length}</span>
            <Button variant="ghost" size="sm" onClick={() => goTo(SLIDES.length - 1)} className="text-slate-400 hover:text-slate-600 text-xs" data-testid="admin-onboard-skip">
              <SkipForward className="w-3.5 h-3.5 mr-1" /> Skip intro
            </Button>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center px-4 md:px-8 pb-2 relative z-10 overflow-y-auto">
          <div className={`w-full max-w-5xl transition-all duration-200 ease-out ${slideClass}`}>
            {slide.isReadyToGo ? (
              /* --- Ready to Go? special layout --- */
              <div className="max-w-2xl mx-auto text-center">
                {showCreateProject ? (
                  /* --- Create Project Form --- */
                  <div className="max-w-md mx-auto text-left" data-testid="create-project-form">
                    <div className="text-center mb-6">
                      <div className="w-12 h-12 rounded-2xl bg-[#5AA2AE]/10 flex items-center justify-center mx-auto mb-3">
                        <FolderPlus className="w-6 h-6 text-[#5AA2AE]" />
                      </div>
                      <h2 className="text-lg font-bold text-slate-900">First, let's create your project</h2>
                      <p className="text-sm text-slate-500 mt-1">You can update these details later.</p>
                    </div>

                    <div className="space-y-4">
                      <div className="space-y-1.5">
                        <Label htmlFor="proj-name" className="text-sm font-medium">Project Name</Label>
                        <Input
                          id="proj-name"
                          autoFocus
                          placeholder="e.g. Office Relocation"
                          value={projectForm.name}
                          onChange={(e) => setProjectForm(p => ({ ...p, name: e.target.value }))}
                          data-testid="onboard-project-name"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="proj-desc" className="text-sm font-medium">Description</Label>
                        <Textarea
                          id="proj-desc"
                          placeholder="Brief description of this project..."
                          value={projectForm.description}
                          onChange={(e) => setProjectForm(p => ({ ...p, description: e.target.value }))}
                          rows={3}
                          data-testid="onboard-project-desc"
                        />
                        <p className="text-xs text-slate-400 flex items-start gap-1.5">
                          <HelpCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#5AA2AE]" />
                          A meaningful description helps the AI contextualize responses.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-2 mt-6">
                      <Button variant="outline" size="sm" onClick={() => setShowCreateProject(false)} className="gap-1">
                        <ChevronLeft className="w-3.5 h-3.5" /> Back
                      </Button>
                      <Button size="sm" onClick={handleCreateProject}
                        disabled={!projectForm.name.trim() || creatingProject}
                        className="flex-1 bg-[#5AA2AE] hover:bg-[#5AA2AE]/90 gap-1"
                        data-testid="onboard-create-project-btn">
                        {creatingProject ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating...</>
                          : <><CheckCircle className="w-4 h-4" /> Create &amp; Continue</>}
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* --- Quick Start / Manual choice --- */
                  <>
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold mb-4"
                      style={{ backgroundColor: `${slide.accent}15`, color: slide.accent }}>
                      <Rocket className="w-3.5 h-3.5" />{slide.subtitle}
                    </div>
                    <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 leading-tight"
                      style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                      {slide.title}
                    </h1>
                    <p className="text-sm md:text-base text-slate-600 leading-relaxed mb-8">{slide.body}</p>

                    <div className="grid sm:grid-cols-2 gap-4 max-w-lg mx-auto">
                      {/* Quick Start option */}
                      <button
                        onClick={handleQuickStart}
                        disabled={!quickStartStatus.eligible || quickStartStatus.loading || importingQuickStart}
                        className={`relative p-6 rounded-2xl border-2 text-left transition-all group
                          ${quickStartStatus.eligible && !importingQuickStart
                            ? 'border-[#5AA2AE] bg-[#5AA2AE]/5 hover:bg-[#5AA2AE]/10 hover:shadow-lg cursor-pointer'
                            : 'border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed'}`}
                        data-testid="quick-start-use-btn"
                      >
                        <div className="w-12 h-12 rounded-xl bg-[#5AA2AE]/10 flex items-center justify-center mb-3">
                          {importingQuickStart
                            ? <Loader2 className="w-6 h-6 text-[#5AA2AE] animate-spin" />
                            : <Rocket className="w-6 h-6 text-[#5AA2AE]" />}
                        </div>
                        <h3 className="text-base font-bold text-slate-900 mb-1">Use Quick Start</h3>
                        <p className="text-xs text-slate-500 leading-relaxed">
                          Pre-populate your project with commonly used questions and drivers.
                        </p>
                        {!quickStartStatus.eligible && !quickStartStatus.loading && (
                          <div className="mt-3 flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 rounded-lg px-2.5 py-1.5">
                            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                            Project already has data
                          </div>
                        )}
                      </button>

                      {/* Configure Manually option */}
                      <button
                        onClick={handleManual}
                        className="relative p-6 rounded-2xl border-2 border-slate-200 bg-white text-left
                          hover:border-slate-300 hover:shadow-lg transition-all cursor-pointer group"
                        data-testid="quick-start-manual-btn"
                      >
                        <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-3 group-hover:bg-slate-200 transition-colors">
                          <Wrench className="w-6 h-6 text-slate-500" />
                        </div>
                        <h3 className="text-base font-bold text-slate-900 mb-1">Configure Manually</h3>
                        <p className="text-xs text-slate-500 leading-relaxed">
                          Start from scratch and build your own questions and drivers.
                        </p>
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              /* --- Normal slide layout --- */
              <div className="grid md:grid-cols-2 gap-5 md:gap-10 items-start">
                {/* Text */}
                <div className="order-2 md:order-1 text-center md:text-left">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold mb-3"
                    style={{ backgroundColor: `${slide.accent}15`, color: slide.accent }}>
                    <slide.icon className="w-3.5 h-3.5" />{slide.subtitle}
                  </div>

                  <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 leading-tight"
                    style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                    {slide.title}
                  </h1>

                  <p className="text-sm md:text-base text-slate-600 leading-relaxed mb-4">{slide.body}</p>

                  {slide.highlights && (
                    <div className="flex flex-wrap gap-1.5 justify-center md:justify-start mb-3">
                      {slide.highlights.map((h, i) => (
                        <Badge key={i} variant="outline" className="text-[10px] py-0.5 px-2 border-slate-200">{h}</Badge>
                      ))}
                    </div>
                  )}

                  {/* Sub-carousel for questions */}
                  {slide.subCarousel === 'questions' && <SubCarousel items={QUESTION_TYPES} type="questions" />}
                  {/* Sub-carousel for dashboards */}
                  {slide.subCarousel === 'dashboards' && <SubCarousel items={DASHBOARD_TYPES} type="dashboards" />}
                </div>

                {/* Image */}
                <div className="order-1 md:order-2 flex justify-center">
                  <div className="relative w-48 h-48 sm:w-64 sm:h-64 md:w-72 md:h-72 rounded-3xl overflow-hidden"
                    style={{ backgroundColor: `${slide.accent}08` }}>
                    <img src={slide.image} alt={slide.title}
                      className="w-full h-full object-contain p-2"
                      style={{ filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.08))' }} />
                    <div className="absolute inset-3 rounded-3xl border-2 opacity-10" style={{ borderColor: slide.accent }} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>

        {/* Footer */}
        <footer className="px-4 md:px-8 pb-4 pt-2 relative z-10">
          {/* Progress bar */}
          <div className="max-w-5xl mx-auto mb-3">
            <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500 ease-out"
                style={{ width: `${((current + 1) / SLIDES.length) * 100}%`, backgroundColor: slide.accent }} />
            </div>
          </div>

          <div className="max-w-5xl mx-auto flex items-center justify-between">
            {/* Grouped dots */}
            <div className="hidden md:flex items-center gap-3 flex-1 overflow-x-auto">
              {sectionGroups.map((group) => (
                <div key={group.label} className="flex items-center gap-1">
                  <span className="text-[9px] text-slate-400 font-medium mr-1 whitespace-nowrap">{group.label}</span>
                  {group.slides.map((s) => (
                    <Tooltip key={s.id}>
                      <TooltipTrigger asChild>
                        <button onClick={() => goTo(s.index)}
                          className={`rounded-full transition-all ${s.index === current ? 'w-5 h-2' : 'w-2 h-2 hover:scale-150'}`}
                          style={{
                            backgroundColor: s.index === current ? slide.accent
                              : s.index < current ? `${slide.accent}50` : '#cbd5e1'
                          }}
                          data-testid={`admin-onboard-dot-${s.index}`} />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs">{s.title}</TooltipContent>
                    </Tooltip>
                  ))}
                </div>
              ))}
            </div>

            {/* Mobile counter */}
            <span className="md:hidden text-xs text-slate-400">{current + 1} / {SLIDES.length}</span>

            {/* Buttons */}
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={prev} disabled={isFirst} className="gap-1" data-testid="admin-onboard-prev">
                <ChevronLeft className="w-4 h-4" /> Back
              </Button>
              {!slide.isReadyToGo && (
                <Button size="sm" onClick={next} className="gap-1 min-w-[110px] text-white"
                  style={{ backgroundColor: slide.accent }} data-testid="admin-onboard-next">
                  {isLast ? <><CheckCircle className="w-4 h-4" /> Let's Go!</> : <>Next <ChevronRight className="w-4 h-4" /></>}
                </Button>
              )}
            </div>
          </div>
        </footer>
      </div>
    </TooltipProvider>
  );
}

// Export walkthrough for use in AdminDashboardPage
export { WalkthroughOverlay, WALKTHROUGH_STEPS, USER_WALKTHROUGH_STEPS };
